import { useState, useMemo, useCallback, useRef } from 'react';
import {
  AvatarStyleDefinition,
  AvatarCategoryGroup,
  avatarStyles,
  groupCategories,
} from '../utils/avatarStyles';
import { buildAvatarUrl } from '../utils/buildAvatarUrl';

export interface AvatarEditorState {
  activeStyleId: string;
  activeStyle: AvatarStyleDefinition;
  currentOptions: Record<string, string>;
  currentUrl: string;
  categoryGroups: AvatarCategoryGroup[];
  hasUnsavedChanges: boolean;
  setOption: (key: string, value: string) => void;
  switchStyle: (styleId: string) => void;
  randomise: () => void;
  initFromConfig: (config: string | undefined) => void;
}

interface SavedConfig {
  styleId: string;
  options: Record<string, string>;
}

function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function useAvatarEditor(): AvatarEditorState {
  const [activeStyleId, setActiveStyleId] = useState<string>('avataaars');
  const [currentOptions, setCurrentOptions] = useState<Record<string, string>>(
    () => ({ ...avatarStyles.avataaars.defaults }),
  );
  const initialConfigRef = useRef<string>('');

  const activeStyle = avatarStyles[activeStyleId];

  const currentUrl = useMemo(
    () => buildAvatarUrl(activeStyle, currentOptions),
    [activeStyle, currentOptions],
  );

  const categoryGroups = useMemo(
    () => groupCategories(activeStyle.categories, currentOptions),
    [activeStyle, currentOptions],
  );

  const hasUnsavedChanges = useMemo(() => {
    const currentConfig = JSON.stringify({
      styleId: activeStyleId,
      options: currentOptions,
    });
    return currentConfig !== initialConfigRef.current;
  }, [activeStyleId, currentOptions]);

  const setOption = useCallback((key: string, value: string) => {
    setCurrentOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const switchStyle = useCallback((styleId: string) => {
    const style = avatarStyles[styleId];
    if (!style) return;
    setActiveStyleId(styleId);
    setCurrentOptions({ ...style.defaults });
  }, []);

  const randomise = useCallback(() => {
    const newOptions: Record<string, string> = {};
    for (const cat of activeStyle.categories) {
      newOptions[cat.key] = getRandomItem(cat.options);
    }
    setCurrentOptions(newOptions);
  }, [activeStyle]);

  const initFromConfig = useCallback((config: string | undefined) => {
    if (!config) {
      const defaultConfig = JSON.stringify({
        styleId: 'avataaars',
        options: avatarStyles.avataaars.defaults,
      });
      initialConfigRef.current = defaultConfig;
      return;
    }

    try {
      const parsed: SavedConfig = JSON.parse(config);
      const style = avatarStyles[parsed.styleId];
      if (style) {
        setActiveStyleId(parsed.styleId);
        setCurrentOptions({ ...style.defaults, ...parsed.options });
        initialConfigRef.current = config;
      }
    } catch {
      // Invalid config, keep defaults
      initialConfigRef.current = JSON.stringify({
        styleId: 'avataaars',
        options: avatarStyles.avataaars.defaults,
      });
    }
  }, []);

  return {
    activeStyleId,
    activeStyle,
    currentOptions,
    currentUrl,
    categoryGroups,
    hasUnsavedChanges,
    setOption,
    switchStyle,
    randomise,
    initFromConfig,
  };
}
